import type { ReactNode } from 'react';

/**
 * A section, as a 25/75 row instead of a card.
 *
 * The Hub had accumulated what the brief calls boxy-container fatigue: every
 * grouping was a filled panel with a border, so a page of six groups was six
 * competing rectangles and the eye had to re-find the hierarchy in each one.
 * This replaces the box with position — a narrow anchor column on the left
 * naming the section, a wide canvas on the right holding it, and a hairline
 * between rows instead of a frame around each.
 *
 * ── What this deliberately does NOT do ─────────────────────────────────────
 *
 * It refuses VISUAL variants. No `variant`, no `tone`, no `padded`. The whole
 * value is that every section on every page sits on the same grid; the moment
 * one caller can opt out of that, the next one does too and the rows stop
 * lining up down the page. Anything that genuinely needs a box is not a
 * SectionRow.
 *
 * It does now take drag handlers, and that is not a softening of the rule
 * above — read on, because the reason is the whole point of this component.
 *
 * ── Why the drag props exist ───────────────────────────────────────────────
 *
 * Projects used to wrap each row in a bare <section> to hold its drag-and-drop
 * handlers. That wrapper broke three CSS rules at once, silently:
 *
 *   .section-rows > .section-row:not(:last-child)  { border-bottom }
 *   .section-rows > .section-row:first-child       { padding-top: 0 }
 *   .section-rows > .section-row:last-child        { padding-bottom: 0 }
 *
 * All three are DIRECT-CHILD selectors, and the wrapper made every row a
 * grandchild. Measured on the live page: `:scope > .section-row` matched ZERO
 * elements, so the dividers between project categories never rendered and the
 * first and last rows kept padding they should not have had.
 *
 * The fix that was NOT taken: relax the combinator, or select by position
 * (`.section-rows > * + *`). Both were tried on paper and both are worse. The
 * class-based direct-child selector is robust precisely because it selects by
 * IDENTITY — it skips anything that is not a row. Admin's <SectionRows> holds
 * an invite dialog whose overlay renders inline as a direct child; the current
 * rules ignore it, and a positional selector would have drawn a hairline
 * across a modal. Selecting by what a thing IS survives an intruder; selecting
 * by where it SITS does not.
 *
 * So the outlier was fixed instead of the contract. Three of the four call
 * sites were already correct; only Projects wrapped. Taking the handlers here
 * lets the wrapper go, which puts Projects back in the same shape as Settings,
 * Files and Admin.
 *
 * Named props rather than a `...rest` spread, deliberately: a spread makes the
 * shared contract open-ended, and the next caller passes something else
 * through it.
 *
 * Below the layout breakpoint the columns stack: a 25% anchor column on a
 * phone is four words wrapped over five lines.
 */

export interface SectionRowProps {
  /** The anchor. Short — it is a label, not a sentence. */
  title: ReactNode;
  /** One line under the title. Optional and usually unnecessary. */
  subtitle?: ReactNode;
  /**
   * Controls that belong to the section rather than to any one item.
   *
   * Rendered in the anchor column beneath the title, NOT floating under the
   * content. That is the third anti-pattern in the brief: an action left
   * dangling below body copy reads as a footnote, and nobody presses it.
   */
  action?: ReactNode;
  /**
   * How the right-hand canvas lays its children out.
   *
   * `list` stacks them with hairlines between — the right shape for rows of
   * text. `grid` is the responsive 1/2/3-column sub-grid, for cards and tiles.
   * Defaulting to `list` because a list inside a filled box is the specific
   * thing this component exists to stop.
   */
  layout?: 'list' | 'grid';
  /**
   * A state class for the row itself — currently only Projects' drag-over
   * highlight. NOT a styling escape hatch: it exists because the highlight
   * used to live on the wrapper this component replaced, and the wrapper's
   * box and the row's box are the same rectangle.
   */
  className?: string | undefined;
  /**
   * Drop-target handlers. A whole category is the drop zone, including its
   * empty space, so they belong on the row rather than on anything inside it.
   *
   * Safe on this element in a way it would not be on a control: these are drag
   * events, so they do not interfere with clicks on the buttons and links
   * nested inside, and a draggable child's own dragstart still bubbles. That
   * is the distinction Drop 5 got wrong on the task table — there the drag
   * target WAS the click target, so the thing you pressed to open a task was
   * the thing that moved when you tried to select it. A row is a container.
   */
  onDragOver?: ((event: React.DragEvent<HTMLElement>) => void) | undefined;
  onDragLeave?: ((event: React.DragEvent<HTMLElement>) => void) | undefined;
  onDrop?: ((event: React.DragEvent<HTMLElement>) => void) | undefined;
  children: ReactNode;
}

export function SectionRow({
  title,
  subtitle,
  action,
  layout = 'list',
  className,
  onDragOver,
  onDragLeave,
  onDrop,
  children,
}: SectionRowProps) {
  return (
    <section
      className={className === undefined ? 'section-row' : `section-row ${className}`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div className="section-row-anchor">
        <h2 className="text-section-anchor">{title}</h2>
        {subtitle === undefined ? null : <p className="text-body-meta">{subtitle}</p>}
        {action === undefined ? null : <div className="section-row-action">{action}</div>}
      </div>
      <div className={`section-row-canvas section-row-canvas--${layout}`}>{children}</div>
    </section>
  );
}

/**
 * The container that makes consecutive rows share one rhythm.
 *
 * The hairline belongs BETWEEN rows, not around them: `border-bottom` on every
 * row would draw a line under the last one, which reads as an unfinished
 * table. Handled in CSS with `:not(:last-child)` rather than by asking each
 * caller to pass `isLast`, because a caller that has to know its own position
 * is a caller that will get it wrong when somebody reorders the page.
 */
export function SectionRows({ children }: { children: ReactNode }) {
  return <div className="section-rows">{children}</div>;
}
